import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getData } from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/endpoints";

const AttendanceContext = createContext(null);

export function AttendanceProvider({ children }) {
    const [userData] = useState(() => JSON.parse(localStorage.getItem("userData") || "{}"));
    const [attendanceData, setAttendanceData] = useState(null);
    const [attendanceVersion, setAttendanceVersion] = useState(0);

    // Silent fetch — updates attendanceData WITHOUT bumping attendanceVersion.
    // Used only on initial mount so dashboards don't get a spurious extra API call.
    const fetchAttendance = useCallback(async () => {
        if (!userData?.id) return;
        try {
            const response = await getData(API_ENDPOINTS.getTodayAttendanceRecord(userData.id));
            if (response.status === "SUCCESS" && response.result) {
                setAttendanceData(response.result);
            } else {
                setAttendanceData(null);
            }
        } catch {
            // silently fail
        }
    }, [userData?.id]);

    // Explicit refresh — called after check-in / check-out.
    // Bumps attendanceVersion so dashboard useEffects re-run and re-fetch their stats.
    const refreshAttendance = useCallback(async () => {
        await fetchAttendance();
        setAttendanceVersion((v) => v + 1);
    }, [fetchAttendance]);

    // Initial load: fetch data silently — no version bump, no extra dashboard call.
    useEffect(() => {
        fetchAttendance();
    }, [fetchAttendance]);

    return (
        <AttendanceContext.Provider value={{ attendanceData, attendanceVersion, refreshAttendance }}>
            {children}
        </AttendanceContext.Provider>
    );
}

export function useAttendance() {
    const ctx = useContext(AttendanceContext);
    if (!ctx) throw new Error("useAttendance must be used inside <AttendanceProvider>");
    return ctx;
}
